import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import classNames from 'classnames';

import { translate } from 'services/i18nService';
import ModalService from 'services/ModalService';
import BoostStore from 'stores/BoostStore';
import FirebaseStore from 'stores/FirebaseStore';
import PaymentStore from 'stores/PaymentStore';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import allUserGroups from 'enum/userGroups';
import { MINUTE_AS_SECONDS, SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import { NEW_SHOWS_STATE } from 'enum/videoPlayer';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { CONTEXTUAL_GOLD_MODAL_AREAS } from 'utils/consts';
import { generateID, secondsToTime, noOp } from 'utils/helpers';
import useInterval from 'hooks/useInterval';
import ThemeService from 'themes/ThemeService';

import crownImageUrl from '../../../images/performerVideo/leader-crown-1.png';
import giftIconUrl from '../../../images/performerVideo/icon-gift-1.png';
import goalIcon from '../../../images/performerVideo/goal-icon-1.png';

import styles from './DesktopSecondTippingRow.css';


const DEFAULT_BOOST_DURATION = 25;
const DEFAULT_BOOST_PRICE = 25;

const IDS = {
  prefix: 'kfu213sS',
  element: 'tipBarSecondRow',
  sendGift: 'sendGift',
  beatTheLeader: 'beatTheLeader',
  completeGoal: 'completeGoal',
  boost: 'boost',
};

const makeId = generateID(IDS.prefix, IDS.element);

// eslint-disable-next-line complexity
const DesktopSecondTippingRow = (props) => {
  const {
    embedProfileV2,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    user,
  } = props;
  const {
    accessHash,
    accessLevel,
    username,
  } = user;

  const [boostTimer, setBoostTimer] = useState(null);

  const { tippingInfo } = TippingStore;
  const { checkFreeBoosts, freeBoost } = BoostStore;
  const { boostRemainingTime, newShowsState } = FirebaseStore;
  const { balance } = PaymentStore;
  const { isPlayerBroadcastMode, realityShow } = ProfileStore;

  const boostPrice = window.BOOSTED_ROOM_PRICE_TOKENS || DEFAULT_BOOST_PRICE;
  const duration = window.BOOSTED_ROOM_DURATION_MINUTES || 5;
  const extendedDuration = window.BOOSTED_ROOM_EXTENDED_DURATION_MINUTES || DEFAULT_BOOST_DURATION;
  const totalDuration = duration + extendedDuration;
  const isGroupShowActive =
    newShowsState === NEW_SHOWS_STATE.INSIDE_GS ||
    newShowsState === NEW_SHOWS_STATE.BONUS_GS ||
    newShowsState === NEW_SHOWS_STATE.GS_SELLING_TICKETS ||
    newShowsState === NEW_SHOWS_STATE.GS_GOAL_REACHED ||
    newShowsState === NEW_SHOWS_STATE.PAUSED_GS;
  const isGold = accessLevel >= ACCESS_LEVEL_NUM.GOLD;
  const boostTheme = ThemeService.theme.boost;

  useEffect(() => {
    if (isGold) {
      checkFreeBoosts({ accessHash, username });
    }
  }, [accessHash, isGold, checkFreeBoosts, username]);

  useInterval(() => {
    const timeLeft = moment(boostRemainingTime).add(extendedDuration, 'minutes').diff(moment(), 'seconds');
    const timePassed = (totalDuration * MINUTE_AS_SECONDS) - timeLeft;
    const formatted = secondsToTime(timePassed);
    if (isNaN(timePassed)) {
      setBoostTimer(null);
    } else {
      setBoostTimer(formatted);
    }
  }, [boostRemainingTime ? SECOND_AS_MILLISECONDS : null]);

  const onOpenBeatLeaderModal = useCallback(() => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.BEAT_THE_LEADER,
      {
        close: ModalService.hideModal,
        embedProfileV2,
        setShowRegistrationModal,
        show: true,
        showBuyTokensModal,
        roomName,
        user,
      }
    );
  }, [
    embedProfileV2,
    roomName,
    setShowRegistrationModal,
    showBuyTokensModal,
    user,
  ]);

  const onCompleteGoal = () => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.DESKTOP_COMPLETE_GOAL_MODAL,
      {
        close: ModalService.hideModal,
        embedProfileV2,
        setShowRegistrationModal,
        show: true,
        showBuyTokensModal,
        roomName,
        user,
        tippingInfoData: TippingStore.tippingInfo,
      }
    );
  };

  const upgradeFromBoostModal = useCallback(() => {
    ModalService.hideModal();

    if (user.accessLevel <= ACCESS_LEVEL_NUM.GUEST_BILLING) {
      ModalService.showModal(
        ModalService.MODAL_KEYS.REGISTRATION_GOLD_MODAL,
        {
          history,
          onClose: ModalService.hideModal,
          showRegistrationGoldModal: true,
        }
      );
    } else {
      ModalService.showModal(
        ModalService.MODAL_KEYS.UPGRADE_TO_GOLD_MODAL,
        { area: CONTEXTUAL_GOLD_MODAL_AREAS.BOOST }
      );
    }
  }, [user]);

  const onBoostClick = useCallback(() => {
    if (boostRemainingTime) {
      return;
    }

    const notEnoughTokens = balance < boostPrice;
    let boostModalSettings = {
      close: ModalService.hideModal,
      showBuyTokensModal,
      roomName,
      user,
    };


    if (!isGold) {
      boostModalSettings = {
        ...boostModalSettings,
        showGoldFeature: true,
        upgradeFromBoostModal,
      };
      if (notEnoughTokens) {
        boostModalSettings.insufficientBalanceError = Boolean(notEnoughTokens);
      }
    } else if (notEnoughTokens) {
      if (freeBoost) {
        boostModalSettings = {
          ...boostModalSettings,
          showGoldFeature: true,
        };
      } else {
        boostModalSettings = {
          ...boostModalSettings,
          insufficientBalanceError: Boolean(notEnoughTokens),
          showGoldFeature: false,
        };
      }
    } else if (freeBoost) {
      boostModalSettings = {
        ...boostModalSettings,
        showGoldFeature: true,
      };
    } else {
      boostModalSettings = {
        ...boostModalSettings,
        showGoldFeature: false,
      };
    }
    ModalService.showModal(
      ModalService.MODAL_KEYS.BOOST_MODAL,
      boostModalSettings
    );
  }, [
    balance,
    boostPrice,
    boostRemainingTime,
    freeBoost,
    isGold,
    roomName,
    showBuyTokensModal,
    upgradeFromBoostModal,
    user,
  ]);

  const onOpenBuyGiftsModal = useCallback(() => {
    ModalService.showModal(
      ModalService.MODAL_KEYS.SEND_QUICK_GIFT_MODAL,
      {
        showClose: false,
        showBuyTokensModal,
        embedProfileV2,
      }
    );
  }, [embedProfileV2, showBuyTokensModal]);

  const boostButtonElement = useMemo(() => {
    if (embedProfileV2 || realityShow?.mainEvent) {
      return null;
    }

    const boostLabel = `${boostTheme.timerLabel}
    ${boostRemainingTime ? translate('profile.boost.active') : ''}
    ${boostTimer || ''}`;

    const boostButtonClasses = classNames({
      [styles.actionButtons]: true,
      [styles.secondRowInteractButtonsDisabled]: Boolean(boostRemainingTime),
    });

    return (
      <Tooltip arrow={true} title={boostLabel}>
        <span>
          <Button
            className={boostButtonClasses}
            disabled={isPlayerBroadcastMode}
            icon={<img alt="boost icon" src={boostTheme.icon} />}
            id={makeId(IDS.boost)}
            onClick={onBoostClick}
          />
        </span>
      </Tooltip>
    );
  }, [
    boostRemainingTime,
    boostTheme,
    boostTimer,
    embedProfileV2,
    isPlayerBroadcastMode,
    realityShow,
    onBoostClick,
  ]);

  const tippingGoal = parseInt(tippingInfo.goal, 10);
  const tippingGoalBalance = parseInt(tippingInfo.goalbalance, 10);
  const completeGoalDisabled = tippingGoal <= tippingGoalBalance || tippingInfo.goal === '';

  const completeGoalClasses = classNames({
    [styles.actionButtons]: true,
    [styles.secondRowInteractButtonsDisabled]: completeGoalDisabled,
  });

  const showSendGiftButton = !embedProfileV2 || UserStore.hasCF(allUserGroups.REALITY_SHOWS);

  return (
    <div className={styles.secondRowInteractButtons}>
      {showSendGiftButton && (
        <Tooltip arrow={true} title={translate('gifting.profile.gift.send.gift')}>
          <span>
            <Button
              className={styles.actionButtons}
              disabled={isPlayerBroadcastMode}
              icon={<img alt="gift" src={giftIconUrl} />}
              id={makeId(IDS.sendGift)}
              onClick={onOpenBuyGiftsModal}
            />
          </span>
        </Tooltip>
      )}
      <Tooltip arrow={true} title={translate('tipping.v2.beat.the.leader')}>
        <span>
          <Button
            className={styles.actionButtons}
            disabled={isPlayerBroadcastMode}
            icon={<img alt="crown" src={crownImageUrl} />}
            id={makeId(IDS.beatTheLeader)}
            onClick={onOpenBeatLeaderModal}
          />
        </span>
      </Tooltip>

      {boostButtonElement}

      {(!isGroupShowActive || true) && (
        <Tooltip arrow={true} title={translate('tipping.tipping.complete.goal')}>
          <span>
            <Button
              className={completeGoalClasses}
              disabled={isPlayerBroadcastMode}
              icon={<img alt="goal" src={goalIcon} />}
              id={makeId(IDS.completeGoal)}
              onClick={completeGoalDisabled ? noOp : onCompleteGoal}
            />
          </span>
        </Tooltip>
      )}
    </div>
  );
};

DesktopSecondTippingRow.propTypes = {
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  roomName: PropTypes.string,
  setShowRegistrationModal: PropTypes.func,
  showBuyTokensModal: PropTypes.func,
};

DesktopSecondTippingRow.defaultProps = {
  embedProfileV2: false,
  roomName: undefined,
  setShowRegistrationModal: () => undefined,
  showBuyTokensModal: undefined,
};

export default observer(DesktopSecondTippingRow);
